import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CalendarEventDto, EnSessionEventView, SessionEventDto } from '@shared/dto';
import { AbstractCrudApiService } from '@shared/abstracts/abstract-crud-api.service';
import { urlJoin } from '@shared/utils/url.utils';

/**
 * Operator-wide events API.
 *
 * Reads every {@link SessionEventDto} the logged-in practitioner can see across
 * all their patients in a date range: `GET /session-events?start&end`.
 *
 * Named generically (`event`, not `session-event`) because the backend models
 * events with table inheritance (`Event` base + `SessionEvent` child, keyed by
 * `type`); other event types will surface through this same slice in the future.
 */
@Injectable({ providedIn: 'root' })
export class EventService extends AbstractCrudApiService<SessionEventDto, any, any> {
  override get endpoint(): string {
    return `${this.api$.value}/:eventId`;
  }

  constructor() {
    super();
    // NOTE: the current operator-wide read lives at `/session-events`. If a
    // polymorphic `/events` endpoint is introduced, only this line changes.
    this.api$.next(urlJoin(this.env.get('api.base'), 'session-events'));
    this.logger.debug(this.name, 'Endpoint', this.endpoint);
  }

  /**
   * Lean calendar read: `GET /session-events?start&end&view=calendar`. Returns the flat
   * {@link CalendarEventDto} projection (no session/exercise/profile graph), so the
   * dashboard/agenda load is a fraction of the full read. Same range filter, same scope.
   */
  readCalendar(start: string, end: string): Observable<CalendarEventDto[]> {
    return this.read(undefined, {
      params: { start, end, view: EnSessionEventView.Calendar },
    }) as unknown as Observable<CalendarEventDto[]>;
  }
}
